import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PrimaryTooltip } from '../../../../../Components/StyledComponent';
import CustomTag from '../../../../../Components/CustomTag';
import { ThemeProperties } from '../../../../../Theme/ThemeProperties';
import { alertTagStyles } from '../../../../../Helper/DigitalTwin/DataFile';
import { EMPTY_CELL } from '../../../../../Helper/BatteryHealth/SohHistoryHelper';

// SOH-specific cell renderer plugged into the generic MonthlyHistoricalPivotTable
// (renderCell). Cell models come from buildSohPivotModel. Text uses the
// generic data table's body variant (fw400s14lh24c3D4B5A, TableColumnValueRenderer
// 'text' / 'number') and the 12px grey caption for second lines.
//
// Backend / DB text (reasons, confidence) is rendered as is, never through
// t(): a ":" in it would be read as an i18n namespace separator. Status labels
// (frontend defaults or org meta) go through t() with the separators off.

// Status tags: CustomTag, the shared tag component behind the generic table's
// 'tag' renderer (TableTagView) and Asset Management's 'status-chip', with
// status-chip's text size / weight (12px / 500) and a fully rounded pill shape
// through CustomTag's containerStyle (the same 12px radius and 12px side
// padding as Inventory's 'iot-status-badge' pill). Colours are the existing
// alertTagStyles entries, not new tokens:
//   computed     -> is_active.active          (success)
//   not_computed -> severity.warning          (warning)
//   forecast     -> bmsAlertStatus['in progress'] (info)
export const SOH_STATUS_TAG_STYLES = {
    computed: alertTagStyles.is_active.active,
    not_computed: alertTagStyles.severity.warning,
    forecast: alertTagStyles.bmsAlertStatus['in progress'],
};
const PILL_STYLE = { borderRadius: '12px', padding: '0 12px' };

// Confidence tags: same CustomTag pill as the status tags, compact padding,
// colours from existing alertTagStyles entries:
//   High   -> is_active.active   (success, as the Computed tag)
//   Medium -> severity.warning   (warning, as the Not computed tag)
//   Low    -> severity.critical  (critical red)
export const SOH_CONFIDENCE_TAG_STYLES = {
    High: alertTagStyles.is_active.active,
    Medium: alertTagStyles.severity.warning,
    Low: alertTagStyles.severity.critical,
};
const COMPACT_PILL_STYLE = { borderRadius: '12px', padding: '0 8px' };

/** Test-id-safe form of a confidence word: lowercase, non-alphanumerics -> "-". */
export const confidenceTestId = (level) =>
    `soh-history-confidence-tag-${String(level).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;

/** Tag colours for a confidence word; unknown words get the neutral grey default. */
export const getConfidenceTagStyle = (level) =>
    SOH_CONFIDENCE_TAG_STYLES[level] || alertTagStyles.is_active.default;

/**
 * One-word confidence pill ("High" / "Medium" / "Low", title-cased); nothing
 * for an empty level. The word is shown as is (backend text, never t()), in the
 * cells and in the help legend alike, so the two always match.
 */
export const ConfidenceTag = ({ level, testId }) => {
    if (!level) return null;
    const style = getConfidenceTagStyle(level);
    return (
        <Box data-testid={testId || confidenceTestId(level)} sx={{ display: 'inline-flex', flexShrink: 0 }}>
            <CustomTag
                text={level}
                backgroundColor={style.backgroundColor}
                fontColor={style.fontColor}
                fontSize="12px"
                fontWeight="500"
                tooltipText={false}
                containerStyle={COMPACT_PILL_STYLE}
            />
        </Box>
    );
};

const ReasonsTooltipTitle = ({ reasons }) => (
    <Box>
        {reasons.map((reason, i) => (
            <Typography key={`${reason}-${i}`} sx={{ fontSize: '12px' }}>{reason}</Typography>
        ))}
    </Box>
);

const SohPivotCell = ({ cell, notComputedLabel }) => {
    const { t } = useTranslation();
    const tLabel = (text) => t(text, { nsSeparator: false, keySeparator: false });
    if (!cell) return <Typography variant="fw400s14lh24c3D4B5A">{EMPTY_CELL}</Typography>;

    switch (cell.kind) {
        case 'status': {
            // The tag, and for Not computed months the full reasons under it as
            // a caption that wraps within the month column.
            const tag = SOH_STATUS_TAG_STYLES[cell.status] || SOH_STATUS_TAG_STYLES.computed;
            const reasons = cell.reasons || [];
            const pill = (
                <Box data-testid={`soh-history-status-chip-${cell.status}`} sx={{ display: 'inline-flex' }}>
                    <CustomTag
                        text={tLabel(cell.text)}
                        backgroundColor={tag.backgroundColor}
                        fontColor={tag.fontColor}
                        fontSize="12px"
                        fontWeight="500"
                        tooltipText={false}
                        containerStyle={PILL_STYLE}
                    />
                </Box>
            );
            if (!reasons.length) return pill;
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    {pill}
                    <Typography variant="fw400s12c686868" data-testid="soh-history-status-reasons">
                        {reasons.join(', ')}
                    </Typography>
                </Box>
            );
        }
        case 'value': {
            // The body variant sets its colour with !important, so the forecast colour must too.
            const isConfidence = cell.subField === 'sohConfidence';
            return (
                // Value and confidence on one line: "96.8%" then a one-word pill.
                // Wraps so a long or unexpected word drops under the value instead
                // of overflowing the 150px month column.
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <Typography
                        variant="fw400s14lh24c3D4B5A"
                        sx={cell.isForecast ? { color: `${ThemeProperties.purple} !important`, fontStyle: 'italic' } : undefined}
                    >
                        {cell.text}
                    </Typography>
                    {cell.subText && (isConfidence
                        ? <ConfidenceTag level={cell.subText} />
                        : <Typography variant="fw400s12c686868">{cell.subText}</Typography>)}
                </Box>
            );
        }
        case 'not_computed': {
            // Entity "—" cells keep their reasons in a tooltip.
            const reasons = cell.reasons || [];
            return (
                <PrimaryTooltip
                    title={reasons.length ? <ReasonsTooltipTitle reasons={reasons} /> : tLabel(notComputedLabel)}
                    placement="top"
                >
                    <Typography variant="fw400s14lh24c3D4B5A" sx={{ cursor: 'help' }}>{cell.text}</Typography>
                </PrimaryTooltip>
            );
        }
        default:
            return <Typography variant="fw400s14lh24c3D4B5A">{cell.text ?? EMPTY_CELL}</Typography>;
    }
};

export default SohPivotCell;

// Confidence help: one line per level with the same pill as the cells.
const CONFIDENCE_HELP_ROWS = [
    { level: 'High', text: 'SOH estimate is reliable' },
    { level: 'Medium', text: 'Estimate is usable; some input data was limited' },
    { level: 'Low', text: 'Estimate is indicative only; input data was sparse' },
];

export const SohConfidenceHelpContent = () => {
    const { t } = useTranslation();
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="soh-history-confidence-help">
            <Typography sx={{ fontSize: '12px', lineHeight: '16px', fontWeight: 600, color: '#fff' }}>
                {t('SOH estimation confidence')}
            </Typography>
            {CONFIDENCE_HELP_ROWS.map(({ level, text }) => (
                <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ConfidenceTag level={level} testId={`soh-history-confidence-help-${level}`} />
                    <Typography sx={{ fontSize: '11px', lineHeight: '16px', color: '#fff' }}>{t(text)}</Typography>
                </Box>
            ))}
        </Box>
    );
};
